// Phase 1 (Founder-First UX) -- the few primitives every founder surface is
// built from. Deliberately small: three action weights (Fitts -- the
// primary action always looks primary), one eyebrow, one stat, one chip,
// one disclosure. Styling comes only from the existing design tokens
// (index.css), so light/dark themes keep working.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { GLIM, GLIMState } from '@/brand';

const focusRing = 'focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]';

export function PrimaryButton({ children, onClick, to, disabled, testId, type = 'button', size = 'md' }) {
  const cls = `inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--accent)] text-white font-semibold ${size === 'lg' ? 'h-11 px-5 text-[14px]' : 'h-10 px-4 text-[13.5px]'} hover:bg-[var(--accent-hover)] active:bg-[var(--accent-active)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${focusRing}`;
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
  return <button type={type} onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>{children}</button>;
}

export function SecondaryButton({ children, onClick, to, disabled, testId, type = 'button' }) {
  const cls = `inline-flex items-center justify-center gap-2 h-10 px-4 rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-primary)] text-[13.5px] font-medium hover:bg-[var(--canvas-tertiary)] disabled:opacity-50 transition-colors ${focusRing}`;
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
  return <button type={type} onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>{children}</button>;
}

export function TextButton({ children, onClick, to, disabled, testId, tone = 'muted' }) {
  const color = tone === 'accent' ? 'text-[var(--accent)]' : tone === 'danger' ? 'text-[var(--error)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]';
  const cls = `inline-flex items-center gap-1 text-[13px] font-medium ${color} hover:underline underline-offset-4 disabled:opacity-50 rounded-sm ${focusRing}`;
  if (to) return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
  return <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testId}>{children}</button>;
}

export function Eyebrow({ children, tone = 'muted', className = '', testId }) {
  const color = tone === 'accent' ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]';
  return <p data-testid={testId} className={`text-[11px] font-semibold uppercase tracking-[0.08em] ${color} ${className}`}>{children}</p>;
}

// Fitts: primary action sits top-right on desktop, full-width under the
// title on mobile -- same place on every surface.
export function PageHeader({ eyebrow, title, summary, primary, secondary, testId }) {
  return (
    <header className="mb-8" data-testid={testId || 'founder-page-header'}>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          {eyebrow && <Eyebrow className="mb-1.5">{eyebrow}</Eyebrow>}
          <h1 className="font-display text-[26px] md:text-[30px] font-bold tracking-tight text-[var(--text-primary)] leading-tight text-balance">{title}</h1>
          {summary && <p className="mt-2 text-[14.5px] text-[var(--text-secondary)] max-w-[62ch] leading-relaxed">{summary}</p>}
        </div>
        {(primary || secondary) && (
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            {secondary}
            {primary}
          </div>
        )}
      </div>
    </header>
  );
}

export function Stat({ value, of, label, tone, testId }) {
  const color = tone === 'bad' ? 'text-[var(--martian-coral,#F06A4F)]' : tone === 'good' ? 'text-[var(--martian-mint-ink,#3F7A64)]' : 'text-[var(--text-primary)]';
  return (
    <div data-testid={testId}>
      <p className={`font-display font-bold tracking-tight tabular-nums ${color}`}>
        <span className="text-[32px] leading-none">{value}</span>
        {of !== undefined && of !== null && <span className="text-[18px] text-[var(--text-tertiary)] font-semibold"> / {of}</span>}
      </p>
      <p className="mt-1.5 text-[13px] text-[var(--text-secondary)] leading-snug max-w-[24ch]">{label}</p>
    </div>
  );
}

export function Chip({ children, tone = 'neutral', testId, title }) {
  const tones = {
    neutral: 'bg-[var(--canvas-tertiary)] text-[var(--text-secondary)]',
    accent: 'bg-[var(--accent-light)] text-[var(--accent)]',
    good: 'bg-[rgba(63,122,100,0.10)] text-[var(--martian-mint-ink,#3F7A64)]',
    warn: 'bg-[rgba(242,215,122,0.35)] text-[var(--martian-yellow-ink,#7A5F12)]',
    bad: 'bg-[rgba(240,106,79,0.12)] text-[#B5452F]',
  };
  return <span title={title} data-testid={testId} className={`inline-flex items-center gap-1 h-6 px-2 rounded-[var(--radius-badge)] text-[12px] font-medium whitespace-nowrap ${tones[tone] || tones.neutral}`}>{children}</span>;
}

// Miller: detail lives one click deeper, never permanently on screen.
export function Disclosure({ label, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={`inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-sm ${focusRing}`}>
        {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        {label}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

export function Section({ eyebrow, title, children, testId, className = '', action }) {
  return (
    <section className={`py-7 border-t border-[var(--border)] first:border-t-0 first:pt-0 ${className}`} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <div>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          {title && <h2 className="font-display text-[18px] font-semibold text-[var(--text-primary)] mt-1 text-balance">{title}</h2>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ title, body, action, testId, glimState = 'neutral' }) {
  return (
    <div className="py-12 text-center max-w-[46ch] mx-auto" data-testid={testId || 'founder-empty'}>
      <div className="mb-3 flex justify-center"><GLIMState state={glimState} size={64} showLabel={false} announce={false} /></div>
      <p className="font-display text-[17px] font-semibold text-[var(--text-primary)]">{title}</p>
      {body && <p className="mt-2 text-[14px] text-[var(--text-secondary)] leading-relaxed">{body}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading…', state = 'thinking' }) {
  return (
    <div className="py-16 flex flex-col items-center gap-2 text-center" role="status" aria-live="polite">
      <GLIMState state={state} size={64} showLabel={false} announce={false} />
      <p className="text-[13px] text-[var(--text-tertiary)]">{label}</p>
    </div>
  );
}

export function AskButton({ onClick, label = 'Ask Martian', testId = 'ask-martian-open', compact }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 h-10 ${compact ? 'w-10 justify-center' : 'px-3.5'} rounded-[var(--radius-button)] border border-[var(--border)] bg-[var(--surface)] text-[13.5px] font-medium text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)] ${focusRing}`}
      aria-label={label}>
      <GLIM size={22} decorative />
      {!compact && <span>{label}</span>}
      {!compact && <kbd className="hidden lg:inline text-[10.5px] font-mono text-[var(--text-tertiary)] border border-[var(--border)] rounded px-1 py-px">Ctrl K</kbd>}
    </button>
  );
}

export const STATUS_COPY = {
  needs_you: { label: 'Needs you', tone: 'accent' },
  in_progress: { label: 'In progress', tone: 'neutral' },
  measuring: { label: 'Measuring', tone: 'good' },
};

export function formatDate(iso, withTime = false) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, withTime
      ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
      : { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function relativeDays(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
